import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import DailyRewards from '../components/DailyRewards';
import { GemIcon } from '../components/GemIcon';
import UltimateTag from '../components/UltimateTag';
import UltimateTagModal from '../components/UltimateTagModal';
import { fallbackAvatarUrl } from '../utils/fallbackAvatar';
import { User, Settings, LogOut, Crown, Sparkles, MessageCircle, ChevronRight, Zap, Bookmark, Camera, XCircle, Pencil, Star, Gift, Flag as FlagIcon, FileText, Trash2, BarChart3, Trophy } from 'lucide-react';
import ModelHealthPill from '../components/ModelHealthPill';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_COLORS = {
  0: '#71717A', 1: '#A1A1AA', 2: '#60A5FA', 3: '#34D399', 4: '#FBBF24',
  5: '#F59E0B', 6: '#F97316', 7: '#EF4444', 8: '#E11D48', 9: '#A855F7', 10: '#E11D48',
};

export default function Profile() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [myCharacters, setMyCharacters] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [collection, setCollection] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [gemsData, setGemsData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('characters');
  const [showRewards, setShowRewards] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [gemCredits, setGemCredits] = useState(null);
  const [redeeming, setRedeeming] = useState(null);
  const [creatorStats, setCreatorStats] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchData();
      fetchGems();
      fetchSubscription();
      fetchGemCredits();
      fetchCreatorStats();
      fetchDrafts();
    }
  }, [user]);

  const fetchDrafts = async () => {
    setDraftsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/character-drafts`, { withCredentials: true });
      setDrafts(data.drafts || []);
    } catch {
      setDrafts([]);
    } finally {
      setDraftsLoading(false);
    }
  };

  const deleteDraft = async (draftId, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/character-drafts/${draftId}`, { withCredentials: true });
      setDrafts(prev => prev.filter(d => d.id !== draftId));
    } catch (err) {
      console.error('Error deleting draft:', err);
    }
  };

  const fetchCreatorStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/creator/stats`, { withCredentials: true });
      setCreatorStats(data);
    } catch (err) {
      console.error('Error fetching creator stats:', err);
    }
  };

  const fetchData = async () => {
    try {
      const [charsRes, convsRes, collRes] = await Promise.all([
        axios.get(`${API_URL}/api/characters/my`, { withCredentials: true }),
        axios.get(`${API_URL}/api/conversations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/user/collection`, { withCredentials: true })
      ]);
      setMyCharacters(charsRes.data);
      setConversations(convsRes.data);
      setCollection(collRes.data);
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/subscription`, { withCredentials: true });
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchGems = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/gems`, { withCredentials: true });
      setGemsData(data);
    } catch (error) {
      console.error('Error fetching gems:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: uploadData } = await axios.post(`${API_URL}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await axios.put(`${API_URL}/api/user/profile`, { avatar: uploadData.url }, { withCredentials: true });
      await checkAuth();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) return;
    setCancelling(true);
    try {
      await axios.post(`${API_URL}/api/subscription/cancel`, {}, { withCredentials: true });
      await fetchSubscription();
    } catch (error) {
      console.error('Cancel error:', error);
      alert(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const fetchGemCredits = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/user/gem-credits`, { withCredentials: true });
      setGemCredits(data);
    } catch (error) {
      console.error('Error fetching gem credits:', error);
    }
  };

  const redeemGems = async (tier) => {
    const cost = tier === 'premium' ? 3000 : 6000;
    if (!window.confirm(`Spend ${cost.toLocaleString()} gems for 100 ${tier === 'premium' ? 'Premium' : 'Ultimate'} messages?`)) return;
    setRedeeming(tier);
    try {
      const { data } = await axios.post(`${API_URL}/api/user/gems/redeem`, { tier }, { withCredentials: true });
      alert(data.message);
      await fetchGems();
      await fetchGemCredits();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to redeem gems');
    } finally {
      setRedeeming(null);
    }
  };

  const deleteCharacter = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this character?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/characters/${id}`, { withCredentials: true });
      setMyCharacters(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting character:', error);
    }
  };

  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || '0';
  };

  const timeAgoShort = (date) => {
    const diff = Math.max(0, Date.now() - date.getTime());
    const s = Math.floor(diff / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return date.toLocaleDateString();
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <User className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Sign in to view profile</h2>
          <p className="text-[#71717A] mb-6">Create characters and manage your chats</p>
          <Link to="/login">
            <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full px-8">
              Sign In
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto">
      {/* Profile Header */}
      <div className="relative px-4 lg:px-6 pt-4 lg:pt-8 pb-6">
        {/* Background Stars Effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-4 right-8 w-1 h-1 bg-white rounded-full animate-pulse" />
          <div className="absolute top-12 right-16 w-0.5 h-0.5 bg-white/60 rounded-full" />
          <div className="absolute top-8 left-12 w-0.5 h-0.5 bg-white/40 rounded-full" />
        </div>

        {/* Top Bar - Gems, Settings, Logout */}
        <div className="flex items-center justify-between mb-4">
          {/* Gems Display */}
          <button
            onClick={() => setShowRewards(true)}
            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#E11D48]/20 to-[#F59E0B]/20 border border-[#E11D48]/30 rounded-full"
            data-testid="gems-button"
          >
            <GemIcon size={18} />
            <span className="text-white font-bold text-sm">{gemsData?.gems || 0}</span>
            {gemsData?.can_claim && (
              <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse" />
            )}
          </button>

          <div className="flex items-center gap-2">
            <Link 
              to="/settings" 
              className="p-2 text-[#71717A] hover:text-white transition-colors" 
              data-testid="settings-button"
            >
              <Settings className="w-5 h-5" />
            </Link>
            <button 
              onClick={handleLogout} 
              className="p-2 text-[#71717A] hover:text-[#E11D48] transition-colors" 
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* User Info */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E11D48] to-[#F59E0B] p-0.5">
              <div className="w-full h-full rounded-full bg-[#1A1D24] flex items-center justify-center overflow-hidden">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-[#A1A1AA]" />
                )}
              </div>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 w-7 h-7 bg-[#E11D48] rounded-full flex items-center justify-center border-2 border-[#050505] hover:bg-[#BE123C] transition-colors"
              data-testid="upload-avatar-btn"
            >
              <Camera className="w-3.5 h-3.5 text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white font-['Unbounded'] flex items-center gap-2 flex-wrap" data-testid="profile-name">
              {user.name}
              {user.ultimate_tag && <UltimateTag tag={user.ultimate_tag} />}
              {user.badge && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#F59E0B] to-[#E11D48] text-white rounded-full align-middle" data-testid="founder-badge">
                  <Crown className="w-3 h-3" /> {user.badge}
                </span>
              )}
            </h1>
            <p className="text-sm text-[#71717A]" data-testid="profile-email">{user.email}</p>
            {user.bio && (
              <p className="text-xs text-[#A1A1AA] mt-1" data-testid="profile-bio">{user.bio}</p>
            )}
            <div className="flex items-center gap-3 mt-1 text-xs text-[#A1A1AA]">
              <span>{myCharacters.length} Characters</span>
              <span>•</span>
              <span>{conversations.length} Chats</span>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button
                onClick={() => navigate('/settings/account')}
                className="flex items-center gap-1.5 px-3 py-1 text-xs text-[#A1A1AA] bg-white/5 border border-white/10 rounded-full hover:bg-white/10 transition-colors"
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-3 h-3" /> Edit Profile
              </button>
              {user.subscription_tier === 'ultimate' && (
                <button
                  onClick={() => setShowTagModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border border-pink-400/40 bg-gradient-to-r from-pink-500/10 to-amber-500/10 text-pink-200 hover:from-pink-500/20 hover:to-amber-500/20 transition-colors"
                  data-testid="edit-ultimate-tag-btn"
                >
                  <Sparkles className="w-3 h-3" /> {user.ultimate_tag ? 'Edit Tag' : 'Set Tag'}
                </button>
              )}
              {creatorStats && creatorStats.level > 0 && (
                <button
                  onClick={() => navigate('/rewards')}
                  className="flex items-center gap-1.5 px-3 py-1 text-xs rounded-full border transition-colors hover:opacity-80"
                  style={{
                    color: LEVEL_COLORS[creatorStats.level],
                    borderColor: `${LEVEL_COLORS[creatorStats.level]}40`,
                    background: `${LEVEL_COLORS[creatorStats.level]}10`,
                  }}
                  data-testid="profile-level-badge"
                >
                  <Gift className="w-3 h-3" /> Lv.{creatorStats.level} {creatorStats.level_name}
                </button>
              )}
              {creatorStats && creatorStats.avg_rating > 0 && (
                <span
                  className="flex items-center gap-1 px-2 py-1 text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-full"
                  data-testid="profile-avg-rating"
                >
                  <Star className="w-3 h-3" /> {creatorStats.avg_rating}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Owner/admin only: chat-model health pill + admin dashboard link */}
      {(user.role === 'owner' || user.role === 'admin') && (
        <>
          <ModelHealthPill />
          <Link
            to="/admin/dashboard"
            className="mx-4 mb-4 flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-[#E11D48]/15 to-[#F59E0B]/10 border border-[#E11D48]/25 hover:from-[#E11D48]/25 hover:to-[#F59E0B]/20 transition-all group"
            data-testid="profile-admin-link"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#E11D48]/25 flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-[#E11D48]" />
              </div>
              <div>
                <p className="text-sm font-bold text-white leading-none">Admin Dashboard</p>
                <p className="text-[10px] text-white/50 mt-0.5">Live stats · users · revenue</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
          </Link>
        </>
      )}

      {/* Membership — compact tier chips that route to /membership */}
      <div className="px-4 lg:px-6 py-4 lg:py-6">
        <div className="flex items-center justify-between mb-3 lg:mb-4">
          <h3 className="text-xs font-medium text-[#71717A] uppercase tracking-wider">Membership</h3>
          <Link to="/membership" className="text-[11px] font-medium text-white/60 hover:text-white" data-testid="membership-compare-link">Compare plans →</Link>
        </div>

        <div className="grid grid-cols-3 gap-2.5">
          {/* Ultimate */}
          <Link
            to="/membership?plan=ultimate"
            className={`relative rounded-2xl p-3 border transition-all active:scale-[0.97] overflow-hidden bg-gradient-to-br from-[#2A0A1F] via-[#4C1D95]/40 to-[#1B0E2E] ${subscription?.tier === 'ultimate' ? 'border-pink-400/70 shadow-[0_0_24px_rgba(236,72,153,0.35)]' : 'border-pink-400/25 hover:border-pink-400/50'}`}
            data-testid="profile-tier-ultimate"
          >
            <Crown className="absolute -top-2 -right-2 w-12 h-12 text-pink-400/15" />
            <p className="text-[10px] font-bold uppercase tracking-wider text-pink-300 font-['Unbounded']">Ultimate</p>
            <p className="mt-1.5 text-lg font-black text-white leading-none">£14.99<span className="text-[10px] text-white/50 font-medium">/mo</span></p>
            {subscription?.tier === 'ultimate' && (
              <span className="absolute bottom-1.5 right-2 text-[8px] font-bold text-pink-300 uppercase tracking-wider">Current</span>
            )}
          </Link>

          {/* Premium */}
          <Link
            to="/membership?plan=premium"
            className={`relative rounded-2xl p-3 border transition-all active:scale-[0.97] overflow-hidden bg-gradient-to-br from-[#2A1A03] via-[#7C2D12]/30 to-[#1F1407] ${subscription?.tier === 'premium' ? 'border-yellow-400/70 shadow-[0_0_24px_rgba(245,158,11,0.35)]' : 'border-yellow-400/25 hover:border-yellow-400/50'}`}
            data-testid="profile-tier-premium"
          >
            <Zap className="absolute -top-2 -right-2 w-12 h-12 text-yellow-400/15" />
            <p className="text-[10px] font-bold uppercase tracking-wider text-yellow-300 font-['Unbounded']">Premium</p>
            <p className="mt-1.5 text-lg font-black text-white leading-none">£9.99<span className="text-[10px] text-white/50 font-medium">/mo</span></p>
            {subscription?.tier === 'premium' && (
              <span className="absolute bottom-1.5 right-2 text-[8px] font-bold text-yellow-300 uppercase tracking-wider">Current</span>
            )}
          </Link>

          {/* Basic */}
          <Link
            to="/membership?plan=basic"
            className={`relative rounded-2xl p-3 border transition-all active:scale-[0.97] overflow-hidden bg-gradient-to-br from-[#1A0E2E]/60 via-[#18181B] to-[#0A0A0A] ${(!subscription?.tier || subscription?.tier === 'free') ? 'border-violet-400/60' : 'border-white/10 hover:border-white/25'}`}
            data-testid="profile-tier-basic"
          >
            <Sparkles className="absolute -top-2 -right-2 w-12 h-12 text-violet-400/15" />
            <p className="text-[10px] font-bold uppercase tracking-wider text-violet-200 font-['Unbounded']">Basic</p>
            <p className="mt-1.5 text-lg font-black text-white leading-none">Free<span className="text-[10px] text-white/50 font-medium"> </span></p>
            {(!subscription?.tier || subscription?.tier === 'free') && (
              <span className="absolute bottom-1.5 right-2 text-[8px] font-bold text-violet-200 uppercase tracking-wider">Current</span>
            )}
          </Link>
        </div>

        {/* Manage Subscription */}
        {subscription?.tier && subscription.tier !== 'free' && (
          <div className="mt-3 p-3 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between">
            <div>
              <p className="text-sm text-white font-medium">
                {subscription.tier === 'premium' ? 'Premium' : 'Ultimate'} Plan Active
              </p>
              {subscription.subscription_permanent ? (
                <p className="text-xs text-[#F59E0B] font-medium">Lifetime Access</p>
              ) : subscription.subscription_end && (
                <p className="text-xs text-[#71717A]">
                  Renews {new Date(subscription.subscription_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>
            {!subscription.subscription_permanent && (
              <Button
                size="sm"
                variant="outline"
                onClick={cancelSubscription}
                disabled={cancelling}
                className="border-[#E11D48]/30 text-[#E11D48] hover:bg-[#E11D48]/10 rounded-full text-xs px-3"
                data-testid="cancel-subscription-btn"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Plan'}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Gem Shop */}
      <div className="mx-4 mb-4 p-4 bg-[#1A1D24] rounded-xl border border-white/5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-white">Gem Shop</h3>
          <div className="flex gap-2 items-center">
            {gemCredits && (
              <div className="flex gap-2">
                {gemCredits.premium_credits > 0 && (
                  <span className="text-xs bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-0.5 rounded-full" data-testid="premium-credits-badge">
                    {gemCredits.premium_credits} Premium msgs
                  </span>
                )}
                {gemCredits.ultimate_credits > 0 && (
                  <span className="text-xs bg-[#E11D48]/10 text-[#E11D48] px-2 py-0.5 rounded-full" data-testid="ultimate-credits-badge">
                    {gemCredits.ultimate_credits} Ultimate msgs
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Buy Gems Banner */}
        <button
          onClick={() => navigate('/gems')}
          className="w-full mb-3 p-3 bg-gradient-to-r from-[#F59E0B]/20 to-[#E11D48]/20 border border-[#F59E0B]/30 rounded-xl flex items-center gap-3 hover:from-[#F59E0B]/30 hover:to-[#E11D48]/30 transition-all active:scale-[0.98]"
          data-testid="buy-gems-btn"
        >
          <div className="w-10 h-10 bg-gradient-to-br from-[#F59E0B] to-[#E11D48] rounded-lg flex items-center justify-center shadow-lg shadow-[#E11D48]/30">
            <GemIcon size={26} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-bold text-white">Buy Gems</p>
            <p className="text-[10px] text-[#71717A]">Get gems to boost characters & unlock models</p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#71717A]" />
        </button>
      </div>

      {/* Creator Center entry — full stats live on a dedicated page */}
      <Link
        to="/creator-center"
        className="mx-4 mb-4 flex items-center justify-between p-3.5 rounded-2xl bg-[#1A1D24] border border-white/5 hover:border-white/15 transition-all group"
        data-testid="profile-creator-center-link"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#E11D48]/15 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#E11D48]" />
          </div>
          <div>
            <p className="text-sm font-bold text-white leading-none">Creator Center</p>
            <p className="text-[10px] text-white/50 mt-0.5">Stats · level · earnings</p>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
      </Link>

      {/* Top Creators link */}
      <Link
        to="/leaderboard"
        className="mx-4 mb-4 flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-yellow-500/10 via-fuchsia-500/10 to-orange-500/10 border border-white/5 hover:border-white/15 transition-all group"
        data-testid="profile-leaderboard-link"
      >
        <div className="w-9 h-9 rounded-xl bg-yellow-500/15 flex items-center justify-center">
          <Trophy className="w-4 h-4 text-yellow-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-none">Top Creators</p>
          <p className="text-[10px] text-white/50 mt-0.5">Most chats · highest rated · top streaks</p>
        </div>
        <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white transition-colors" />
      </Link>

      {/* Tabs */}
      <div className="flex border-b border-white/10 mx-4 lg:mx-6">
        <button
          onClick={() => setActiveTab('characters')}
          className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'characters' ? 'text-white' : 'text-[#71717A]'
          }`}
          data-testid="tab-characters"
        >
          Characters
          {activeTab === 'characters' && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#E11D48] rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('drafts')}
          className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'drafts' ? 'text-white' : 'text-[#71717A]'
          }`}
          data-testid="tab-drafts"
        >
          <span className="inline-flex items-center gap-1.5">
            Drafts
            {drafts.length > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#F59E0B]">
                {drafts.length}
              </span>
            )}
          </span>
          {activeTab === 'drafts' && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#E11D48] rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('collection')}
          className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'collection' ? 'text-white' : 'text-[#71717A]'
          }`}
          data-testid="tab-collection"
        >
          Collection
          {activeTab === 'collection' && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#E11D48] rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('chats')}
          className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
            activeTab === 'chats' ? 'text-white' : 'text-[#71717A]'
          }`}
          data-testid="tab-chats"
        >
          Chats
          {activeTab === 'chats' && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#E11D48] rounded-full" />
          )}
        </button>
      </div>

      {/* Tab Content */}
      <div className="px-4 lg:px-6 py-4 lg:py-6">
        {activeTab === 'characters' ? (
          myCharacters.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
              <p className="text-[#71717A] mb-4">No characters created yet</p>
              <Link to="/create">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full">
                  Create Your First
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {myCharacters.map((char) => {
                const isFlagged = !!char.flagged;
                return (
                <Link key={char.id} to={`/character/${char.id}`} className="block">
                  <div className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    isFlagged
                      ? 'bg-[#1A0610] border-[#E11D48]/40 hover:bg-[#220815]'
                      : 'bg-[#1A1D24] border-white/5 hover:bg-[#1A1D24]/80'
                  }`} data-testid={`my-char-${char.id}`}>
                    <div className="relative w-14 h-14 flex-shrink-0">
                      <img
                        src={char.avatar_url || fallbackAvatarUrl(char.name)}
                        alt={char.name}
                        className={`w-14 h-14 rounded-lg object-cover ${isFlagged ? 'blur-md scale-105' : ''}`}
                        onError={(e) => { e.target.src = fallbackAvatarUrl(char.name); }}
                      />
                      {isFlagged && (
                        <>
                          <div className="absolute inset-0 rounded-lg bg-[#E11D48]/30 ring-2 ring-[#E11D48]/60 flex items-center justify-center">
                            <FlagIcon className="w-5 h-5 text-white drop-shadow animus-flag-wave" />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-bold text-sm ${isFlagged ? 'text-[#FCA5A5]' : 'text-white'}`}>{char.name}</h3>
                        {isFlagged ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#E11D48] text-white animus-flag-pulse" data-testid={`flagged-${char.id}`}>
                            <FlagIcon className="w-3 h-3 animus-flag-wave" /> Flagged
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 text-[10px] bg-white/10 rounded text-[#71717A]">
                            {char.is_public ? 'Public' : 'Private'}
                          </span>
                        )}
                      </div>
                      {isFlagged ? (
                        <p className="text-xs text-[#FB7185]/80 mt-0.5">Image flagged for explicit nudity — tap to fix</p>
                      ) : (
                        <p className="text-xs text-[#71717A] mt-0.5">
                          {formatCount(char.chat_count)} Messages
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.preventDefault(); navigate(`/character/${char.id}/edit`); }}
                        className="p-2 text-[#71717A] hover:text-[#F59E0B] transition-colors"
                        data-testid={`edit-char-${char.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => deleteCharacter(char.id, e)}
                        className="p-2 text-[#71717A] hover:text-[#E11D48] transition-colors"
                        data-testid={`delete-char-${char.id}`}
                      >
                        •••
                      </button>
                    </div>
                  </div>
                </Link>
                );
              })}
            </div>
          )
        ) : activeTab === 'drafts' ? (
          draftsLoading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-white/10 border-t-[#E11D48] rounded-full animate-spin mx-auto" />
            </div>
          ) : drafts.length === 0 ? (
            <div className="text-center py-8" data-testid="drafts-empty">
              <FileText className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
              <p className="text-[#71717A] mb-1">No drafts yet</p>
              <p className="text-[11px] text-[#52525B] mb-4 max-w-xs mx-auto">Start creating a character — your progress will auto-save here so you can finish them whenever.</p>
              <Link to="/create">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full">
                  Start a Character
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="drafts-list">
              {drafts.map((d) => {
                const fd = d.form_data || {};
                const tagCount = Array.isArray(fd.tags) ? fd.tags.length : 0;
                const hasDesc = !!(fd.description && fd.description.trim());
                const hasGreet = !!(fd.greeting && fd.greeting.trim());
                const hasPers = !!(fd.personality && fd.personality.trim());
                // Crude completeness: avatar + name + desc + greet + pers + tags = 6 steps
                const steps = [!!fd.avatar_url, !!(fd.name && fd.name.trim()), hasDesc, hasGreet, hasPers, tagCount > 0];
                const completed = steps.filter(Boolean).length;
                const pct = Math.round((completed / steps.length) * 100);
                const updated = d.updated_at ? new Date(d.updated_at) : null;
                const timeAgo = updated ? timeAgoShort(updated) : '';
                return (
                  <Link key={d.id} to={`/create?draft=${d.id}`} className="block" data-testid={`draft-item-${d.id}`}>
                    <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-[#1A1D24] to-[#15171D] rounded-xl border border-[#F59E0B]/15 hover:border-[#F59E0B]/40 transition-colors">
                      <div className="relative w-14 h-14 flex-shrink-0">
                        {fd.avatar_url ? (
                          <img src={fd.avatar_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#2A1A03] to-[#7C2D12]/40 border border-[#F59E0B]/20 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-[#F59E0B]" />
                          </div>
                        )}
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#F59E0B] text-black text-[9px] font-bold flex items-center justify-center shadow">
                          {pct}%
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-sm text-white truncate max-w-[180px]">{d.title || 'Untitled draft'}</h3>
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/25">
                            Draft
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 max-w-[140px] h-1 bg-white/5 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#F59E0B] to-[#E11D48] rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-[#71717A]">{timeAgo}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => deleteDraft(d.id, e)}
                        className="p-2 text-[#71717A] hover:text-[#E11D48] transition-colors"
                        data-testid={`delete-draft-${d.id}`}
                        title="Delete draft"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </Link>
                );
              })}
              <Link to="/create" className="block">
                <div className="flex items-center justify-center gap-2 p-3 border border-dashed border-white/15 rounded-xl text-[#71717A] hover:text-white hover:border-white/30 transition-colors text-xs">
                  <Sparkles className="w-3.5 h-3.5" />
                  Start a new character
                </div>
              </Link>
            </div>
          )
        ) : activeTab === 'collection' ? (
          collection.length === 0 ? (
            <div className="text-center py-8">
              <Bookmark className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
              <p className="text-[#71717A] mb-4">No characters collected</p>
              <Link to="/">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full">
                  Browse Characters
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {collection.map((char) => (
                <Link key={char.id} to={`/character/${char.id}`} className="block">
                  <div className="flex items-center gap-3 p-3 bg-[#1A1D24] rounded-xl border border-white/5 hover:bg-[#1A1D24]/80 transition-colors">
                    <img
                      src={char.avatar_url || fallbackAvatarUrl(char.name)}
                      alt={char.name}
                      className="w-14 h-14 rounded-lg object-cover"
                      onError={(e) => { e.target.src = fallbackAvatarUrl(char.name); }}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-sm">{char.name}</h3>
                      <p className="text-xs text-[#71717A] truncate mt-0.5">{char.description}</p>
                    </div>
                    <Bookmark className="w-4 h-4 text-[#E11D48]" />
                  </div>
                </Link>
              ))}
            </div>
          )
        ) : (
          conversations.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
              <p className="text-[#71717A] mb-4">No conversations yet</p>
              <Link to="/">
                <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full">
                  Browse Characters
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {conversations.slice(0, 10).map((conv) => (
                <Link key={conv.id} to={`/chat/${conv.id}`} className="block">
                  <div className="flex items-center gap-3 p-3 bg-[#1A1D24] rounded-xl border border-white/5 hover:bg-[#1A1D24]/80 transition-colors">
                    <img
                      src={conv.character_avatar || fallbackAvatarUrl(conv.character_name)}
                      alt={conv.character_name}
                      className="w-14 h-14 rounded-full object-cover"
                      onError={(e) => { e.target.src = fallbackAvatarUrl(conv.character_name); }}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-sm">{conv.character_name}</h3>
                      <p className="text-xs text-[#71717A] truncate mt-0.5">{conv.last_message}</p>
                    </div>
                    <span className="text-[10px] text-[#71717A]">
                      {new Date(conv.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )
        )}
      </div>

      {/* Follow us — socials */}
      <div className="mx-4 mt-2 mb-6 flex flex-col items-center gap-3 py-6 border-t border-white/5" data-testid="profile-socials">
        <p className="text-[11px] font-bold tracking-widest uppercase text-[#71717A]">Follow Animus</p>
        <div className="flex items-center gap-3">
          <a
            href="https://www.tiktok.com/@projectanimus"
            target="_blank"
            rel="noopener noreferrer"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white/[0.04] hover:bg-white/[0.1] border border-white/[0.08] hover:border-white/[0.2] text-white/70 hover:text-white transition-all duration-300 hover:scale-110"
            aria-label="Follow Animus on TikTok"
            data-testid="profile-social-tiktok"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.84a8.16 8.16 0 0 0 4.77 1.52V6.91a4.85 4.85 0 0 1-1.84-.22Z"/>
            </svg>
          </a>
          <a
            href="https://www.instagram.com/animuschat/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-11 h-11 flex items-center justify-center rounded-full bg-white/[0.04] hover:bg-white/[0.1] border border-white/[0.08] hover:border-white/[0.2] text-white/70 hover:text-white transition-all duration-300 hover:scale-110"
            aria-label="Follow Animus on Instagram"
            data-testid="profile-social-instagram"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </a>
        </div>
        <p className="text-[10px] text-[#71717A]">@animuschat · @projectanimus</p>
      </div>

      {/* Daily Rewards Modal */}
      <DailyRewards 
        isOpen={showRewards} 
        onClose={() => {
          setShowRewards(false);
          fetchGems(); // Refresh gems after closing
        }} 
      />

      {/* Ultimate Tag Modal */}
      {showTagModal && (
        <UltimateTagModal
          currentTag={user?.ultimate_tag}
          onClose={() => setShowTagModal(false)}
          onSaved={() => checkAuth()}
        />
      )}
      </div>
    </div>
  );
}



